import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { ProfileContext } from "../../../context/ProfileProvider";
import "../PasskeyPage/passkey.css";
import "./bindGoogleAuthenticator.css";

const maskEmail = (email) => {
  if (!email) return "";
  const [local, domain] = String(email).split("@");
  if (!domain) return email;
  const visible = Math.min(2, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
};

const BindGoogleAuthenticator = () => {
  const navigate = useNavigate();
  const profileCtx = useContext(ProfileContext) || {};
  const userDetails = useMemo(() => profileCtx.userDetails || {}, [profileCtx.userDetails]);
  const handleUserDetails = profileCtx.handleUserDetails || (() => { });

  const emailId = userDetails?.emailId || "";
  const mobileNumber = userDetails?.mobileNumber || "";
  const hasEmail = !!emailId;
  const [isSecurityRiskMethodsOpen, setIsSecurityRiskMethodsOpen] = useState(false);

  const [qrImage, setQrImage] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [passkeySupported, setPasskeySupported] = useState(false);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [passkeyVerified, setPasskeyVerified] = useState(false);
  const silentPasskeyAttempted = useRef(false);

  // Email verify popup state
  const [isVerifyEmailOpen, setIsVerifyEmailOpen] = useState(false);
  const [emailVerifiedForSetup, setEmailVerifiedForSetup] = useState(false);
  const [emailOtp, setEmailOtp] = useState(["", "", "", "", "", ""]);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const isEmailOtpComplete = emailOtp.every((d) => d !== "");
  const initSentRef = useRef(false);

  const canConfirm = authCode.length === 6 && (passkeyVerified || emailVerifiedForSetup || !hasEmail);

  // ============ Passkey support check ============
  const checkPasskeySupport = useCallback(async () => {
    if (window.PublicKeyCredential === undefined || typeof window.PublicKeyCredential !== "function") {
      setPasskeySupported(false);
      return false;
    }
    try {
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setPasskeySupported(!!available);
      return !!available;
    } catch {
      setPasskeySupported(false);
      return false;
    }
  }, []);

  const fetchPasskeys = useCallback(async () => {
    try {
      const result = await AuthService.passkeyGetList();
      if (result?.success && result?.data != null) {
        const d = result.data;
        const n =
          d.count != null
            ? Number(d.count)
            : (Array.isArray(d.passkeys) ? d.passkeys.length : 0);
        setHasPasskey(n > 0);
      }
    } catch {
      // silent
    }
  }, []);

  const attemptSilentPasskeyVerification = useCallback(async () => {
    try {
      const optionsResult = await AuthService.passkeyGetStepUpOptions();
      if (!optionsResult?.success || !optionsResult?.data) return null;
      let credential;
      try {
        credential = await startAuthentication(optionsResult.data);
      } catch {
        return null;
      }
      const verifyResult = await AuthService.passkeyVerifyStepUp(credential);
      if (verifyResult?.success) return verifyResult.data;
      return null;
    } catch {
      return null;
    }
  }, []);

  const loadQr = useCallback(async () => {
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.security2faSetup();
      if (result?.success) {
        setQrImage(result.data?.qr_code || "");
        setSecretKey(result.data?.secret?.base32 || "");
      } else {
        alertErrorMessage(result?.message || "Failed to load QR code");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, []);

  const sendEmailOtp = useCallback(async () => {
    if (!hasEmail) return false;
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securitySendOtp("email", "2fa_setup");
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        setResendTimer(60);
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send verification code");
      return false;
    } catch (error) {
      alertErrorMessage(error?.message || "Failed to send verification code");
      return false;
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  }, [hasEmail]);

  useEffect(() => {
    checkPasskeySupport();
    fetchPasskeys();
  }, [checkPasskeySupport, fetchPasskeys]);

  // Root prereq: email must be verified to enable Google Authenticator
  useEffect(() => {
    if (!userDetails) return;
    if (!hasEmail) {
      alertErrorMessage("Please verify your email first before enabling Google Authenticator.");
      navigate("/user_profile/security/emailVerification", { replace: true });
    }
  }, [userDetails, hasEmail, navigate]);

  // Initial flow: silent passkey try → else open email verify popup
  useEffect(() => {
    if (!hasEmail) return;
    if (silentPasskeyAttempted.current) return;
    silentPasskeyAttempted.current = true;

    (async () => {
      if (hasPasskey && passkeySupported) {
        const vr = await attemptSilentPasskeyVerification();
        if (vr) {
          setPasskeyVerified(true);
          alertSuccessMessage("Passkey verified");
          loadQr();
          return;
        }
      }
      // No passkey or failed → open email verify popup + auto-send OTP
      if (!initSentRef.current) {
        initSentRef.current = true;
        const ok = await sendEmailOtp();
        if (ok) {
          setIsVerifyEmailOpen(true);
          requestAnimationFrame(() => document.getElementById("bga-email-otp-0")?.focus());
        }
      }
    })();
  }, [hasEmail, hasPasskey, passkeySupported, attemptSilentPasskeyVerification, loadQr, sendEmailOtp]);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleCopyKey = () => {
    if (!secretKey) return;
    navigator.clipboard
      .writeText(secretKey)
      .then(() => alertSuccessMessage("Key copied"))
      .catch(() => alertErrorMessage("Failed to copy key"));
  };

  // ============ Email verify popup handlers ============
  const handleEmailOtpInput = (idx, e) => {
    const v = String(e?.target?.value ?? "").replace(/\D/g, "").slice(0, 1);
    setEmailOtp((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) {
      document.getElementById(`bga-email-otp-${idx + 1}`)?.focus();
    }
  };

  const handleEmailOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !emailOtp[idx] && idx > 0) {
      document.getElementById(`bga-email-otp-${idx - 1}`)?.focus();
    }
  };

  const handleInputPasteEmailOtp = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const digits = String(pasteData || "").replace(/\D/g, "").slice(0, 6).split("");
    if (digits.length > 0) {
      setEmailOtp((prev) => {
        const next = [...prev];
        digits.forEach((d, i) => {
          if (i < 6) next[i] = d;
        });
        return next;
      });
      window.requestAnimationFrame(() => {
        const targetIdx = digits.length === 6 ? 5 : digits.length;
        document.getElementById(`bga-email-otp-${targetIdx}`)?.focus();
      });
    }
  };

  const handlePasteEmailOtp = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = String(text || "").replace(/\D/g, "").slice(0, 6).split("");
      if (digits.length < 6) return;
      setEmailOtp([digits[0], digits[1], digits[2], digits[3], digits[4], digits[5]]);
      document.getElementById("bga-email-otp-5")?.focus();
    } catch {
      // silent
    }
  };

  const handleResendEmailOtp = async () => {
    if (resendTimer > 0) return;
    await sendEmailOtp();
  };

  const handleConfirmEmailVerification = async () => {
    if (!isEmailOtpComplete) return;
    const otp = emailOtp.join("");
    try {
      setIsVerifyingEmail(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.securityVerifyOtp("email", otp, "2fa_setup");
      if (result?.success) {
        alertSuccessMessage("Email verified");
        setEmailVerifiedForSetup(true);
        setIsVerifyEmailOpen(false);
        loadQr();
      } else {
        alertErrorMessage(result?.message || "Invalid verification code");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsVerifyingEmail(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const closeVerifyEmail = () => {
    setIsVerifyEmailOpen(false);
    setIsSecurityRiskMethodsOpen(false);
    setEmailOtp(["", "", "", "", "", ""]);
    // Redirect back to main Security Settings page on close
    navigate("/user_profile/security", { replace: true });
  };

  const availableMethods = useMemo(() => {
    const list = [];
    if (hasPasskey && passkeySupported) {
      list.push({
        id: "passkey",
        label: "Passkey",
        icon: "/images/security/passkey_icon.svg",
        onClick: async () => {
          setIsSecurityRiskMethodsOpen(false);
          LoaderHelper.loaderStatus(true);
          try {
            const vr = await attemptSilentPasskeyVerification();
            if (vr) {
              setEmailVerifiedForSetup(true);
              setIsVerifyEmailOpen(false);
              setEmailOtp(["", "", "", "", "", ""]);
              alertSuccessMessage("Verified with passkey");
              loadQr();
            } else {
              alertErrorMessage("Passkey could not be used. Please enter the email code.");
            }
          } finally {
            LoaderHelper.loaderStatus(false);
          }
        },
      });
    }
    // Always offer phone/SMS path so “Choose other verification method” is not hidden when user has no passkey yet
    list.push({
      id: "phone",
      label: "Phone Number",
      icon: "/images/security/phone_icon2.svg",
      onClick: () => {
        setIsSecurityRiskMethodsOpen(false);
        setIsVerifyEmailOpen(false);
        setEmailOtp(["", "", "", "", "", ""]);
        navigate("/user_profile/security/smsVerification");
      },
    });
    return list;
  }, [hasPasskey, passkeySupported, attemptSilentPasskeyVerification, loadQr, navigate]);

  // ============ Final confirm: enable 2FA with TOTP ============
  const handleConfirm = async () => {
    if (!canConfirm) return;
    try {
      setIsLoading(true);
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.security2faConfirm(authCode);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Google Authenticator enabled successfully");
        handleUserDetails();
        navigate("/user_profile/security", { replace: true });
      } else {
        alertErrorMessage(result?.message || "Failed to enable 2FA");
      }
    } catch (error) {
      alertErrorMessage(error?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const currentTimeStr = new Date().toLocaleString("en-CA", { hour12: false }).replace(",", "");
  const maskedEmailDisplay = maskEmail(emailId);

  return (
    <main className="bga-page" aria-labelledby="bga-title">
      <nav className="bga-page__crumbs" aria-label="Breadcrumb">
        <ol className="bga-page__crumbList">
          <li>
            <button type="button" className="bga-page__crumbLink" onClick={() => navigate("/user_profile/security")}>
              Security
            </button>
          </li>
          <li className="bga-page__crumbSep" aria-hidden="true">
            ›
          </li>
          <li className="bga-page__crumbItem--active" aria-current="page">
            Google Authenticator
          </li>
        </ol>
      </nav>

      <h1 id="bga-title" className="bga-page__title">
        Bind Google Authenticator
      </h1>

      <div className="bga-page__grid">
        <div>
          {/* Step 1 */}
          <div className="bga-step">
            <span className="bga-step__num">1</span>
            <div>
              <h3 className="bga-step__heading">Install Google Authenticator on your smartphone</h3>
              <div className="bga-apps">
                <a
                  className="bga-apps__card"
                  href="https://apps.apple.com/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="bga-apps__card-left">
                    <span className="bga-apps__logo" aria-hidden="true">
                      <i className="ri-apple-fill" />
                    </span>
                    <span>
                      <div className="bga-apps__title">App Store</div>
                      <div className="bga-apps__sub">Download from</div>
                    </span>
                  </span>
                  <span className="bga-apps__qr" aria-hidden="true">
                    <i className="ri-qr-code-line" />
                  </span>
                </a>

                <a
                  className="bga-apps__card"
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="bga-apps__card-left">
                    <span className="bga-apps__logo" aria-hidden="true">
                      <i className="ri-google-play-fill" />
                    </span>
                    <span>
                      <div className="bga-apps__title">Google Play</div>
                      <div className="bga-apps__sub">Download from</div>
                    </span>
                  </span>
                  <span className="bga-apps__qr" aria-hidden="true">
                    <i className="ri-qr-code-line" />
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bga-step">
            <span className="bga-step__num">2</span>
            <div>
              <h3 className="bga-step__heading">Setup Google Authenticator</h3>
              <p className="bga-step__note">Note: Please properly keep the Google verification key.</p>
              <div className="bga-qr">
                <div className="bga-qr__img">
                  {qrImage ? <img src={qrImage} alt="Google Authenticator QR code" /> : <span style={{ fontSize: 11, color: "#707a8a" }}>Loading…</span>}
                </div>
                <div className="bga-qr__key">Key:{secretKey || "—"}</div>
                <button type="button" className="bga-qr__copy" onClick={handleCopyKey} aria-label="Copy key">
                  <i className="ri-file-copy-line" />
                </button>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="bga-step">
            <span className="bga-step__num">3</span>
            <div>
              <h3 className="bga-step__heading">Input the 6-digits dynamic code from your Google Authenticator</h3>
              <p className="bga-step__note">
                In the Google Authenticator, Click + to add new account. You may scan the QR code or enter provided key to add your account on
                Google Authenticator.
              </p>

              <div className="bga-field">
                <label className="bga-field__label" htmlFor="bga-auth-code">
                  Google Authenticator Code
                </label>
                <div className="bga-field__control">
                  <input
                    id="bga-auth-code"
                    className="bga-field__input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Please Enter"
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  />
                </div>
                <p className="bga-field__hint">6 digits code on your Google Authenticator</p>
              </div>

              <button
                type="button"
                className={`bga-page__confirm ${canConfirm ? "is-ready" : ""}`}
                disabled={!canConfirm || isLoading}
                onClick={handleConfirm}
              >
                {isLoading ? "Please wait…" : "Confirm"}
              </button>

              <div>
              </div>
            </div>
          </div>
        </div>

        <aside className="bga-notes">
          <h3 className="bga-notes__heading">Notes:</h3>

          <p className="bga-notes__item">
            1. Do not delete the Google verification code account in the Google Authenticator app, otherwise you will be restricted from account
            operations. If you are unable to enter the Google verification code due to phone loss, software uninstallation, etc., please contact
            the customer support via Email:{" "}
            <a className="bga-notes__link" href="mailto:support@agcx.com">
              support@agcx.com
            </a>
          </p>

          <p className="bga-notes__item">
            2. If it keeps prompting wrong Google verification code, please check and calibrate the phone time. The current time:{" "}
            <strong>{currentTimeStr}</strong>
          </p>

          <p className="bga-notes__item">
            3. Google authentication adds a second layer of protection to your fund's safety. After enabling this feature, you will be required to
            enter the Google verification code every time you log in, or withdraw assets. This feature is currently available for iOS and Android
            devices.
          </p>

          <p className="bga-notes__item">
            Please read the following document before installing Google verification:{" "}
            <button
              type="button"
              className="bga-notes__link"
              style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}
              onClick={() => { }}
            >
              Google Authenticator installation and usage instructions
            </button>
          </p>
        </aside>
      </div>

      {/* ============ Verify Email Popup (opens on landing) ============ */}
      {isVerifyEmailOpen ? (
        <div className="passkey-modal-overlay" role="presentation">
          <div
            className="passkey-modal passkey-verify-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bga-verify-email-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={closeVerifyEmail}
            >
              ×
            </button>

            <h2 id="bga-verify-email-title" className="passkey-verify-modal__title">
              Verify Your Email
            </h2>
            <p className="passkey-verify-modal__subtitle">
              The verification code has been sent to your email {maskedEmailDisplay}, valid for 10 minutes.
            </p>

            <div className="passkey-verify-modal__otpRow" aria-label="Verification code">
              {emailOtp.map((digit, idx) => (
                <input
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  id={`bga-email-otp-${idx}`}
                  className="passkey-verify-modal__otp"
                  value={digit}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  autoComplete={idx === 0 ? "one-time-code" : undefined}
                  aria-label={`Digit ${idx + 1}`}
                  onInput={(e) => handleEmailOtpInput(idx, e)}
                  onChange={(e) => handleEmailOtpInput(idx, e)}
                  onKeyDown={(e) => handleEmailOtpKeyDown(idx, e)}
                  onPaste={handleInputPasteEmailOtp}
                />
              ))}
            </div>

            <div className="passkey-verify-modal__links">
              <button
                type="button"
                className="passkey-verify-modal__link"
                onClick={handleResendEmailOtp}
                disabled={resendTimer > 0 || isVerifyingEmail}
                style={resendTimer > 0 ? { opacity: 0.55, cursor: "not-allowed", textDecoration: "none" } : undefined}
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend"}
              </button>
              <button
                type="button"
                className="passkey-verify-modal__link passkey-verify-modal__link--paste"
                onClick={handlePasteEmailOtp}
              >
                Paste{" "}
                <span aria-hidden="true">
                  <i className="ri-file-copy-fill" />
                </span>
              </button>
            </div>

            <button
              type="button"
              className="passkey-verify-modal__confirm"
              disabled={!isEmailOtpComplete || isVerifyingEmail}
              onClick={handleConfirmEmailVerification}
            >
              {isVerifyingEmail ? "Verifying…" : "Confirm"}
            </button>

            <div className="passkey-verify-modal__footerRow">
              {availableMethods.length > 0 ? (
                <button
                  type="button"
                  className="passkey-verify-modal__chooseOther"
                  onClick={() => setIsSecurityRiskMethodsOpen(true)}
                >
                  Choose other verification method
                </button>
              ) : null}
              <button
                type="button"
                className="passkey-verify-modal__unable"
                onClick={() => navigate("/asset_managemnet/security_verification_unavailable")}
              >
                Security verification unavailable?
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isSecurityRiskMethodsOpen && availableMethods.length > 0 ? (
        <div className="passkey-modal-overlay passkey-modal-overlay--onTop" role="presentation">
          <div
            className="passkey-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bga-security-risk-title"
          >
            <button
              type="button"
              className="passkey-modal__close"
              aria-label="Close"
              onClick={() => setIsSecurityRiskMethodsOpen(false)}
            >
              ×
            </button>
            <div className="passkey-modal__art" aria-hidden="true">
              <img className="dark_img" src="/images/security/security_risk_vector.svg" alt="" />
              <img className="light_img" src="/images/security/security_risk_vector_light.svg" alt="" />
            </div>
            <h2 id="bga-security-risk-title" className="passkey-modal__title">
              Security Risk Warning
            </h2>
            <p className="passkey-modal__subtitle">
              To enhance your account security, please activate at least one additional verification method.
            </p>
            <div className="passkey-modal__list" role="list">
              {availableMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className="passkey-modal__item"
                  role="listitem"
                  onClick={method.onClick}
                >
                  <span className="passkey-modal__itemLeft">
                    <span className="passkey-modal__itemIcon" aria-hidden="true">
                      <img src={method.icon} alt="" />
                    </span>
                    <span className="passkey-modal__itemText">{method.label}</span>
                  </span>
                  <span className="passkey-modal__chev" aria-hidden="true">
                    ›
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="passkey-verify-modal__unable"
              style={{ marginTop: 16 }}
              onClick={() => navigate("/asset_managemnet/security_verification_unavailable")}
            >
              Security verification unavailable?
            </button>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default BindGoogleAuthenticator;
